/**
 * Process Control read/write client (docs/control/API.md). Types mirror the server payloads; nothing here invents a
 * field the API does not return. Every payload is for a simulated plant.
 */
import { api, authedFetch } from "./api.js";

export type Quality = "GOOD" | "UNCERTAIN" | "BAD";
export interface TagView {
  unit: string;
  tag: string;
  value: number | boolean;
  status: Quality;
  eu: string | null;
  /** URS-CTL-044: where the value comes from; an unbound I/O tag is "simulated", a derived one "calculated". */
  source?: "simulated" | "opcua" | "modbus" | "calculated";
  quality?: "GOOD" | "BAD" | "SIMULATED" | null;
  /** The channel is a SIMULATED field controller (apps/sim-plc): never shown as real hardware. */
  simulatedDevice?: boolean;
}
export interface UnitIo {
  unit: string;
  mode: "SIMULATED" | "MIXED" | "HARDWARE";
  bound: string[];
  unbound: string[];
  /** Bound to a SIMULATED field controller: real driver path, no real equipment. */
  simulatedDevice?: boolean;
  devices: { kind: string; healthy: boolean; lastError: string | null }[];
}
export interface AlarmRow {
  unit: string;
  id: string;
  state: string;
  priority: number;
  type: string;
  limit: number | null;
  consequence: string | null;
  response: string | null;
  changedAt: string | null;
  awaitingAck: boolean;
  /** Set by the server for a restricted person when the alarm belongs to another area (GAP I1). */
  readOnly?: true;
}
export interface ClockInfo {
  mode: string;
  factor: number;
  runClass: string;
  gmpLinked: boolean;
  simulated: boolean;
  simTimeSeconds: number;
}
export interface ReleaseRef {
  id: string;
  no: number;
  hash: string;
  activatedAt: string;
}
export interface ControlSnapshot {
  state: string;
  release: ReleaseRef | null;
  clock: ClockInfo;
  tags: TagView[];
  alarms: AlarmRow[];
  /** Per unit: SIMULATED, MIXED (some signals bound to hardware) or HARDWARE. */
  io?: UnitIo[];
}
export interface ModuleStat {
  unit: string;
  module: string;
  periodMs: number;
  scans: number;
  avgMs: number;
  worstMs: number;
  overruns: number;
}
/** One SIMULATED hot-standby controller pair (packages/control/src/redundancy.ts). */
export interface RedundancyView {
  unit: string;
  simulated: true;
  label: string;
  primary: "A" | "B";
  standby: "A" | "B" | null;
  state: "SYNCED" | "LAGGING" | "SIMPLEX" | "FAULT_DETECT";
  lagMs: number;
  maxLagMs: number;
  syncEveryMs: number;
  syncs: number;
  failovers: number;
  primaryFault: "CRASH" | "HANG" | null;
  blockedStandbyWrites: number;
  lastFailover: {
    reason: "CRASH" | "HEARTBEAT_MISSED";
    from: "A" | "B";
    to: "A" | "B";
    lagMs: number;
    atEngineMs: number;
  } | null;
}
export interface ControlHealth {
  state: string;
  redundancy?: RedundancyView[];
  refusal: string | null;
  release: ReleaseRef | null;
  loop: { tickMs: number; lastTickAt: string | null; maxLagMs: number; stalls: number; clampedMs: number };
  clock: ClockInfo;
  modules: ModuleStat[];
  historian: {
    depth: number;
    capacity: number;
    written: number;
    dropped: number;
    totalDropped: number;
    rejected: number;
    lastError: string | null;
  };
  events: { queued: number; lastError: string | null };
}
export interface HistoryPoint {
  t: string;
  v: number;
  s: Quality;
  k: string;
}
export interface HistoryResult {
  simulated: boolean;
  training: boolean;
  truncated: boolean;
  series: Record<string, HistoryPoint[]>;
}
export interface CommandRow {
  id: string;
  label: string;
  unit: string;
  tag: string;
  value: number | boolean;
  permitted: boolean;
  blockedBy: { id: string; text: string }[];
  permissivesNow: { id: string; text: string; ok: boolean }[];
}
export interface SigningInfo {
  requestId: string;
  subjectType: "control_request" | "control_alarm_request";
  subjectId: string;
  streamId: string;
  meaning: "PERFORMED";
  state: unknown;
  expiresInMinutes: number;
}
export type WriteResult =
  | { applied: true; unit: string; tag: string; old: number | boolean; new: number | boolean }
  | { applied: false; signatureRequired: true; signing: SigningInfo };
export interface ModuleAction {
  id: string;
  at: string;
  tag: string;
  action: string;
  old: number | boolean | null;
  new: number | boolean | null;
  outcome: "APPLIED" | "REFUSED";
  refusalReason: string | null;
  reason: string | null;
  by: string;
  signed: boolean;
  training: boolean;
}
export interface HistorianTagSummary {
  tag: string;
  deadband: number;
  eu: string | null;
  heartbeatS: number;
  samples: number;
  oldest: string | null;
  newest: string | null;
  gaps: { start: string; end: string; reason: string }[];
}
/** The parts of a configuration release the screens read (units, modules, blocks, alarms, historian, commands). */
export interface ReleaseContentView {
  units: {
    id: string;
    modules: { id: string; blocks: Record<string, unknown>[]; alarms?: Record<string, unknown>[] }[];
    historian: { tag: string; deadband: number; eu?: string; heartbeatS?: number }[];
    signedWrites: string[];
    commands: { id: string; label: string; tag: string; value: number | boolean }[];
  }[];
}

export interface PidTuning {
  kc: number;
  tiS: number;
  tdS: number;
}
export interface LoopHealthDetail {
  samples: number;
  durationS: number;
  stdDevError: number;
  manualPct: number;
  saturationPct: number;
  iae: number;
  oscillation: { detected: boolean; periodS: number | null; amplitude: number | null; cycles: number };
  stiction: { state: "likely" | "unlikely" | "not-assessed"; zeroLagRatio: number | null };
  verdict: "healthy" | "attention" | "poor" | "not-assessable";
  findings: string[];
  summary: string;
}
export interface LoopHealthRow {
  tag: string;
  unit: string;
  module: string;
  tuning: PidTuning;
  dtS: number;
  health: LoopHealthDetail | null;
  reason: string | null;
}
export interface LoopHealthResult {
  simulated: boolean;
  training: boolean;
  windowMin: number;
  loops: LoopHealthRow[];
}
export interface TuningProposalView {
  method: "SIMC" | "lambda";
  structure: "PI" | "PID";
  action: "reverse" | "direct";
  proposed: PidTuning;
  why: string;
  expectedImprovement: { text: string };
}
export type IdentifyResult =
  | { sandbox: true; identified: false; refusal: { code: string; reason: string } }
  | {
      sandbox: true;
      identified: true;
      model: { gain: number; tauS: number; deadS: number };
      fit: { r2: number; label: "good" | "fair" };
      snr: number;
      current: PidTuning;
      actionMismatch: boolean;
      proposals: TuningProposalView[];
    };

const BASE = "/api/v1/control";

/** Event chronicle (URS-CTL-025): one control event with its position in the hash chain. */
export interface ChronicleRow {
  eventId: string;
  at: string;
  unit: string;
  tag: string;
  kind: string;
  type: string | null;
  who: string;
  old: unknown;
  new: unknown;
  value: unknown;
  reason: string | null;
  training: boolean;
  chain: { streamId: string; position: number; globalSeq: number; contentHash: string; prevHash: string };
  link: { type: "alarm" | "interlock" | "release"; id: string } | null;
}
export interface ChroniclePage {
  tz: string;
  rows: ChronicleRow[];
  nextCursor: string | null;
}
export interface ChronicleQuery {
  from: Date;
  to: Date;
  unit?: string;
  tag?: string;
  types?: string[];
  q?: string;
  cursor?: string;
}
/** Historian aggregates (URS-CTL-024), computed on the server over stored samples. */
export interface AggregateRow {
  tag: string;
  bucketStart: string;
  min: number;
  max: number;
  avg: number;
  stddev: number | null;
  count: number;
  percentGood: number;
}
export interface AggregateResult {
  intervalSeconds: number;
  basis: string;
  rows: AggregateRow[];
}
export type AggInterval = "1m" | "5m" | "15m" | "1h" | "6h" | "1d";

const chronicleParams = (q: ChronicleQuery): string => {
  const p = new URLSearchParams({ from: q.from.toISOString(), to: q.to.toISOString() });
  if (q.unit) p.set("unit", q.unit);
  if (q.tag) p.set("tag", q.tag);
  if (q.types?.length) p.set("types", q.types.join(","));
  if (q.q) p.set("q", q.q);
  return p.toString();
};
const aggParams = (tags: string[], from: Date, to: Date, interval: AggInterval): string =>
  `tags=${tags.map(encodeURIComponent).join(",")}&from=${from.toISOString()}&to=${to.toISOString()}&interval=${interval}`;

export interface LoopPoint {
  signal: string;
  channel: string;
  direction: "OUTPUT" | "INPUT";
  expected: number | boolean | null;
  read: number | boolean | null;
  tolerance: number;
  pass: boolean;
  note: string;
  restored: boolean | null;
}
export interface LoopCheckRecord {
  checkId: string;
  at: string;
  unit: string;
  by: string;
  reason: string;
  pass: boolean;
  passed: number;
  failed: number;
  tolerancePct: number;
  mode: string;
  simulatedDevice: boolean;
  deviceLabel: string;
  points: LoopPoint[];
  chain: { position: number; globalSeq: number; contentHash: string; prevHash: string };
}

export const control = {
  loopChecks: (unit?: string) =>
    api.get<LoopCheckRecord[]>(`${BASE}/loop-checks${unit ? `?unit=${encodeURIComponent(unit)}` : ""}`),
  runLoopCheck: (unit: string, reason: string) =>
    api.post<LoopCheckRecord>(`${BASE}/loop-checks`, { unit, reason }),
  snapshot: () => api.get<ControlSnapshot>(`${BASE}/snapshot`),
  health: () => api.get<ControlHealth>(`${BASE}/health`),
  alarms: (state: "active" | "shelved" | "all" = "all") =>
    api.get<AlarmRow[]>(`${BASE}/alarms?state=${state}`),
  acknowledge: (id: string) =>
    api.post<{ id: string }>(`${BASE}/alarms/${encodeURIComponent(id)}/acknowledge`),
  history: (tags: string[], from: Date, to: Date) =>
    api.get<HistoryResult>(
      `${BASE}/history?tags=${tags.map(encodeURIComponent).join(",")}&from=${from.toISOString()}&to=${to.toISOString()}&limit=20000`,
    ),
  release: (id: string) => api.get<{ id: string; content: ReleaseContentView }>(`${BASE}/releases/${id}`),
  commands: () => api.get<CommandRow[]>(`${BASE}/commands`),
  runCommand: (id: string) => api.post<WriteResult>(`${BASE}/commands/${encodeURIComponent(id)}/run`),
  write: (body: { tag: string; value: number | boolean; reason?: string }) =>
    api.post<WriteResult>(`${BASE}/writes`, body),
  sign: (requestId: string, body: { payloadHash: string; idToken: string }) =>
    api.post<WriteResult>(`${BASE}/requests/${requestId}/sign`, body),
  moduleActions: (block: string) =>
    api.get<ModuleAction[]>(`${BASE}/modules/${encodeURIComponent(block)}/actions?limit=15`),
  historian: () => api.get<HistorianTagSummary[]>(`${BASE}/historian`),
  events: (q: ChronicleQuery) =>
    api.get<ChroniclePage>(
      `${BASE}/events?${chronicleParams(q)}&limit=100${q.cursor ? `&cursor=${q.cursor}` : ""}`,
    ),
  aggregates: (tags: string[], from: Date, to: Date, interval: AggInterval) =>
    api.get<AggregateResult>(`${BASE}/history/aggregates?${aggParams(tags, from, to, interval)}`),
  /** The audited server-side CSV (the export is recorded with who, filter and file hash). */
  exportEvents: (q: ChronicleQuery) => fetchCsv(`${BASE}/events/export?${chronicleParams(q)}`),
  exportAggregates: (tags: string[], from: Date, to: Date, interval: AggInterval) =>
    fetchCsv(`${BASE}/history/aggregates/export?${aggParams(tags, from, to, interval)}`),
  loopHealth: (windowMin: number) => api.get<LoopHealthResult>(`${BASE}/loops/health?windowMin=${windowMin}`),
  identify: (tag: string) =>
    api.post<IdentifyResult>(`${BASE}/loops/${encodeURIComponent(tag)}/identify`, {}),
  draftTuning: (tag: string, body: PidTuning & { reason: string; changeControlRef: string }) =>
    api.post<{ id: string; release_no: number }>(
      `${BASE}/loops/${encodeURIComponent(tag)}/draft-release`,
      body,
    ),
};

async function fetchCsv(path: string): Promise<string> {
  return (await authedFetch(path)).text();
}
